import React from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { enableNotificationDestination, disableNotificationDestination } from 'Shared/actions/notifications';
import { updateNotificationDestinationSettings } from 'Shared/actions/notifications';
import StyledCheckbox from 'Shared/components/Checkboxes/StyledCheckbox/StyledCheckbox';
import { WpContainer, WpContainerBody, WpContainerHeader } from 'Wpds/components/container';

import { WpButton } from 'Wpds/components/button';

// Use Notifications API from browser
// https://developer.mozilla.org/en-US/docs/Web/API/Notifications_API
function UserNotificationsBrowser() {
  const dispatch = useDispatch();
  const notificationDestination = useSelector(state =>
    state.notification_destinations.find(({ slug }) => slug === 'browser')
  );
  const browserNotificationPermitted = Notification.permission === 'granted';

  const handleChange = event => {
    const updatedValues = {
      ...notificationDestination.settings,
      [event.target.name]: !notificationDestination.settings[event.target.name],
    };
    dispatch(
      updateNotificationDestinationSettings({
        notificationDestinationID: notificationDestination.id,
        settings: updatedValues,
      })
    );
  };

  const enableBrowserNotifications = () => {
    dispatch(enableNotificationDestination({ notification_slug: 'browser' }));
  };

  const disableBrowserNotification = () => {
    dispatch(disableNotificationDestination({ notification_slug: 'browser' }));
  };

  // result can be "denied" or "granted" or "default"
  const askBrowserNotificationPermission = () => {
    if (browserNotificationPermitted) {
      enableBrowserNotifications();
    } else {
      Notification.requestPermission().then(result => {
        if (result === 'granted') {
          enableBrowserNotifications();
        }
      });
    }
  };

  if (!notificationDestination) return null;

  return (
    <WpContainer>
      <WpContainerHeader>
        <h5>Browser Notifications</h5>
      </WpContainerHeader>
      <WpContainerBody>
        {browserNotificationPermitted && notificationDestination.enabled ? (
          <>
            <p className="mb-4">Customize your browser notifications settings</p>

            <div className="d-flex flex-column mb-3">
              <StyledCheckbox
                id="activity"
                name="activity"
                label="Task Notifications"
                checked={notificationDestination.settings.activity}
                onChange={handleChange}
              />
              <div className="ml-4 text-muted">
                Notifies you when new action items or discussion topics are assigned to you, and when action items are
                past due
              </div>
            </div>
            <div className="d-flex flex-column mb-3">
              <StyledCheckbox
                id="comments"
                name="comments"
                label="Comment Notifications"
                checked={notificationDestination.settings.comments}
                onChange={handleChange}
              />
              <div className="ml-4 text-muted">
                Receive updates when someone adds a new comment or @mentions you in a comment
              </div>
            </div>
            <div className="d-flex flex-column mb-3">
              <StyledCheckbox
                id="feedback"
                name="feedback"
                label="Feedback and Kudos Notifications"
                checked={notificationDestination.settings.feedback}
                onChange={handleChange}
              />
              <div className="ml-4 text-muted">Notifies you when you receive feedback and kudos</div>
            </div>
            <div className="d-flex flex-column mb-3">
              <StyledCheckbox
                id="meeting_reminder_one_hour"
                name="meeting_reminder_one_hour"
                label="1:1 reminder one hour in advance"
                checked={notificationDestination.settings.meeting_reminder_one_hour}
                onChange={handleChange}
              />
              <div className="ml-4 text-muted">
                Reminds you to prepare for your 1:1 meetings one hour before the meeting
              </div>
            </div>
            <div className="d-flex flex-column mb-3">
              <StyledCheckbox
                id="meeting_reminder_one_day"
                name="meeting_reminder_one_day"
                label="1:1 reminder one day in advance"
                checked={notificationDestination.settings.meeting_reminder_one_day}
                onChange={handleChange}
              />
              <div className="ml-4 text-muted">
                Reminds you to prepare for your 1:1 meetings one day before the meeting
              </div>
            </div>
            <div className="d-flex flex-column mb-3">
              <StyledCheckbox
                id="group_meeting_reminder_one_hour"
                name="group_meeting_reminder_one_hour"
                label="Group meeting reminder one hour in advance"
                checked={notificationDestination.settings.group_meeting_reminder_one_hour}
                onChange={handleChange}
              />
              <div className="ml-4 text-muted">
                Reminds you to prepare for your group meetings one hour before the meeting
              </div>
            </div>
            <div className="d-flex flex-column mb-3">
              <StyledCheckbox
                id="group_meeting_reminder_one_day"
                name="group_meeting_reminder_one_day"
                label="Group meeting reminder one day in advance"
                checked={notificationDestination.settings.group_meeting_reminder_one_day}
                onChange={handleChange}
              />
              <div className="ml-4 text-muted">
                Reminds you to prepare for your group meetings one day before the meeting
              </div>
            </div>
            <WpButton variant="secondary" onClick={disableBrowserNotification}>
              Disable Browser Notifications
            </WpButton>
          </>
        ) : (
          <>
            <WpButton onClick={askBrowserNotificationPermission}>Enable Browser Notifications</WpButton>
          </>
        )}
      </WpContainerBody>
    </WpContainer>
  );
}

export default UserNotificationsBrowser;
